import React, { useState } from 'react';

// Tooltip content function - ALWAYS USED
const getSectionTooltipContent = (section) => {
    const tooltips = {
        education: "Education scores evaluate academic qualifications, institution reputation, and alignment with job requirements. Higher scores indicate better educational fit.",
        experience: "Experience scores assess relevance, depth, and career progression. Scores reflect how well past roles match the target position.",
        skills: "Skills scores measure technical competencies, certifications, and proficiency levels. Higher scores indicate stronger skill alignment.",
        projects: "Project scores evaluate complexity, relevance, and outcomes. They assess practical application of skills in real-world scenarios.",
        leadership: "Leadership scores measure management experience, team size, and influence. Higher scores indicate stronger leadership capabilities.",
        research: "Research scores assess publications, academic contributions, and research methodology. Higher scores indicate stronger research background.",
        certifications: "Certification scores evaluate professional credentials, recency, and relevance. Higher scores indicate better credential alignment.",
        awards: "Award scores measure recognition, prestige, and selectivity. Higher scores indicate more prestigious achievements.",
        publications: "Publication scores assess academic output, impact, and relevance. Higher scores indicate stronger academic contributions."
    };
    return tooltips[section.toLowerCase()] || "This section evaluates various aspects relevant to the job requirements.";
};

// Weight style helper - ALWAYS USED
const getWeightStyle = (weight) => {
    const percentage = parseFloat(weight) * 100;
    let color;
    if (percentage >= 20) color = "#4CAF50"; // Green for high weight
    else if (percentage >= 10) color = "#2196F3"; // Blue for medium weight
    else color = "#FF9800"; // Orange for low weight
    
    return {
        color,
        percentage: percentage.toFixed(1)
    };
};

// Circle style helper - ALWAYS USED
const getCircleStyle = (score) => {
    let gradient, opacity;
    
    if (score >= 1.5) {
        gradient = "linear-gradient(135deg, #4ade80, #22c55e)";
        opacity = 0.6 + ((score - 1.5) / 0.5) * 0.4;
    } else if (score >= 1.0) {
        gradient = "linear-gradient(135deg, #fbbf24, #f59e0b)";
        opacity = 0.6 + ((score - 1.0) / 0.4) * 0.4;
    } else {
        gradient = "linear-gradient(135deg, #f87171, #ef4444)";
        opacity = 0.6 + (score / 0.9) * 0.4;
    }
    
    return { 
        background: gradient, 
        opacity: Math.min(Math.max(opacity, 0.6), 1.0)
    };
};

// Calculate average subfield score for a section
const calculateAverageSubfieldScore = (sectionData) => {
    if (!sectionData || typeof sectionData !== "object") return 0;
    
    const numericScores = Object.entries(sectionData).filter(([field, value]) =>
        field !== "comment" && field !== "cross_section_analysis" && typeof value === "number"
    );
    
    if (numericScores.length === 0) return 0;
    
    const totalScore = numericScores.reduce((sum, [field, value]) => sum + value, 0);
    return (totalScore / numericScores.length).toFixed(1);
};

// Professional Info Tooltip Component
const InfoTooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);

    return (
        <div 
            className="info-tooltip-container"
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
        >
            {children}
            {isVisible && (
                <div className="info-tooltip">
                    <div className="info-tooltip-content">
                        <div className="info-tooltip-header">
                            <span className="info-tooltip-icon">ℹ️</span>
                            <span className="info-tooltip-title">Section Explanation</span>
                        </div>
                        <div className="info-tooltip-body">
                            <p>{content}</p>
                        </div>
                        <div className="info-tooltip-arrow"></div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Main Unified Dashboard Component
const UnifiedDashboard = ({ sectionWeights = {}, subfieldScores = {}, detailedReasoning = {} }) => {
    const [expandedSections, setExpandedSections] = useState({});
    
    // Force all functions to be used to prevent tree shaking
    React.useEffect(() => {
        console.log("UnifiedDashboard loaded with functions:", {
            getSectionTooltipContent: getSectionTooltipContent('test'),
            getWeightStyle: getWeightStyle(0.5),
            getCircleStyle: getCircleStyle(1.0)
        });
    }, []);

    const toggleReasoning = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    return (
        <div className="unified-sections-dashboard">
            {Object.entries(sectionWeights).map(([section, weight]) => {
                const weightStyle = getWeightStyle(weight);
                const sectionData = subfieldScores[section];
                const averageScore = calculateAverageSubfieldScore(sectionData);
                const reasoning = detailedReasoning[section];
                const isExpanded = expandedSections[section];
                
                if (!sectionData || typeof sectionData !== "object") {
                    return (
                        <div key={section} className="professional-section-card">
                            <div className="section-weight-header">
                                <div className="section-title-container">
                                    <h4 className="section-title">
                                        {section.charAt(0).toUpperCase() + section.slice(1).replace(/_/g, " ")}
                                        <InfoTooltip content={getSectionTooltipContent(section)}>
                                            <span className="info-icon">ℹ️</span>
                                        </InfoTooltip>
                                    </h4>
                                    <div className="section-weight-badge-container">
                                        <span className="section-weight-badge" style={{background: `linear-gradient(135deg, ${weightStyle.color}, ${weightStyle.color}dd)`}}>
                                            {weightStyle.percentage}% Section Weight
                                        </span>
                                        {reasoning && (
                                            <button 
                                                className="expand-reasoning-btn"
                                                onClick={() => toggleReasoning(section)}
                                                title="View detailed reasoning"
                                            >
                                                📝 Click to view detailed reasoning
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="weight-visualization">
                                    <div 
                                        className="weight-circle" 
                                        style={{
                                            background: `conic-gradient(${weightStyle.color} 0deg, ${weightStyle.color} ${parseFloat(weight) * 360}deg, #e0e0e0 ${parseFloat(weight) * 360}deg, #e0e0e0 360deg)`
                                        }}
                                    >
                                        <span className="weight-text">{weightStyle.percentage}%</span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Expandable reasoning section */}
                            {reasoning && (
                                <div className={`reasoning-comments ${isExpanded ? 'expanded' : 'collapsed'}`}>
                                    <p className="reasoning-text">{reasoning.comment || reasoning}</p>
                                </div>
                            )}
                            
                            <div className="no-data">No subfield data available</div>
                        </div>
                    );
                }
                
                // Get numeric scores only
                const numericScores = Object.entries(sectionData).filter(([field, value]) =>
                    field !== "comment" && field !== "cross_section_analysis" && typeof value === "number"
                );
                
                return (
                    <div key={section} className="professional-section-card">
                        <div className="section-weight-header">
                            <div className="section-title-container">
                                <h4 className="section-title">
                                    {section.charAt(0).toUpperCase() + section.slice(1).replace(/_/g, " ")}
                                    <InfoTooltip content={getSectionTooltipContent(section)}>
                                        <span className="info-icon">ℹ️</span>
                                    </InfoTooltip>
                                </h4>
                                <div className="section-weight-badge-container">
                                    <span className="section-weight-badge" style={{background: `linear-gradient(135deg, ${weightStyle.color}, ${weightStyle.color}dd)`}}>
                                        {weightStyle.percentage}% Section Weight
                                    </span>
                                    {reasoning && (
                                        <button 
                                            className="expand-reasoning-btn"
                                            onClick={() => toggleReasoning(section)}
                                            title="View detailed reasoning"
                                        >
                                            📝 Click to view detailed reasoning
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="weight-visualization">
                                <div 
                                    className="weight-circle" 
                                    style={{
                                        background: `conic-gradient(${weightStyle.color} 0deg, ${weightStyle.color} ${parseFloat(weight) * 360}deg, #e0e0e0 ${parseFloat(weight) * 360}deg, #e0e0e0 360deg)`
                                    }}
                                >
                                    <span className="weight-text">{weightStyle.percentage}%</span>
                                </div>
                                <div className="average-score-container">
                                    <div className="average-score-label">Avg Score</div>
                                    <div className="average-score-circle" style={getCircleStyle(parseFloat(averageScore))}>
                                        <span className="average-score-text">{averageScore}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Expandable reasoning section */}
                        {reasoning && (
                            <div className={`reasoning-comments ${isExpanded ? 'expanded' : 'collapsed'}`}>
                                <p className="reasoning-text">{reasoning.comment || reasoning}</p>
                            </div>
                        )}
                        
                        <div className="subfield-scores-grid">
                            {numericScores.map(([field, value]) => {
                                const circleStyle = getCircleStyle(value);
                                return (
                                    <div key={field} className="subfield-metric-card">
                                        <div className="metric-header">
                                            <span className="metric-label">
                                                {field.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                                            </span>
                                            <span className="metric-score-badge">{value}/2.0</span>
                                        </div>
                                        <div className="metric-score-container">
                                            <div 
                                                className="metric-score-circle" 
                                                style={circleStyle}
                                                title={`${value}/2.0`}
                                            >
                                                <span className="metric-score-text">{value}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default UnifiedDashboard;
